import { useState } from 'react';
import { Monitor, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import type { HostSummary } from '../api/hosts';
import { Button } from './Button';
import { Card } from './Card';
import { Badge } from './Badge';

interface HostCardProps {
  host: HostSummary;
  onConnect: (host: HostSummary) => void;
  onDelete: (host: HostSummary) => Promise<void>;
  busy?: boolean;
}

type Mode = 'idle' | 'confirming' | 'deleting' | 'error';

function relativeTime(ms: number | null): string {
  if (ms === null) return 'never';
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

export function HostCard({ host, onConnect, onDelete, busy }: HostCardProps) {
  const [mode, setMode] = useState<Mode>('idle');

  async function confirmDelete() {
    setMode('deleting');
    try {
      await onDelete(host);
      // success: parent refresh removes this card (component unmounts)
    } catch {
      setMode('error');
    }
  }

  const confirming = mode !== 'idle';

  return (
    <Card
      variant="flat"
      role="listitem"
      tabIndex={host.online ? 0 : -1}
      onKeyDown={(e) => {
        if (mode === 'idle' && host.online && e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onConnect(host);
        } else if (confirming && e.key === 'Escape' && mode !== 'deleting') {
          setMode('idle');
        }
      }}
      className={clsx(
        'flex flex-col gap-4 p-5 transition-[box-shadow,border-color] duration-[var(--duration-base)]',
        confirming
          ? 'border-danger/60'
          : host.online
            ? 'hover:border-border-strong hover:shadow-[var(--shadow-2)]'
            : 'opacity-70',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[var(--radius-md)] border border-border-subtle bg-[linear-gradient(180deg,var(--color-bg-overlay),var(--color-bg-raised))]">
            <Monitor className="h-5 w-5 text-text-secondary" />
          </div>
          <div className="min-w-0 space-y-0.5">
            <h3 className="truncate font-medium leading-tight text-text-primary">{host.name}</h3>
            <p className="text-xs text-text-muted">
              last seen{' '}
              <time dateTime={host.lastSeenAt ? new Date(host.lastSeenAt).toISOString() : ''}>
                {relativeTime(host.lastSeenAt)}
              </time>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {host.online
            ? <Badge variant="success" pulse>online</Badge>
            : <Badge variant="muted">offline</Badge>}
          <button
            type="button"
            aria-label="Delete host"
            onClick={(e) => { e.stopPropagation(); setMode('confirming'); }}
            className="rounded-[var(--radius-sm)] p-1 text-text-muted transition-colors hover:text-danger"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {confirming ? (
        <div className="space-y-2">
          <p className="text-sm text-text-secondary" aria-live="polite">
            Delete this host?
            {mode === 'error' && (
              <span className="ml-1 text-danger">Couldn&apos;t delete. Try again.</span>
            )}
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              className="flex-1"
              disabled={mode === 'deleting'}
              onClick={() => setMode('idle')}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="flex-1"
              loading={mode === 'deleting'}
              onClick={() => { void confirmDelete(); }}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant={host.online ? 'primary' : 'secondary'}
          disabled={!host.online}
          loading={busy}
          onClick={() => onConnect(host)}
          className="w-full"
        >
          Connect
        </Button>
      )}
    </Card>
  );
}
